import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Switch, Segmented, message as antMessage } from "antd";
import { fetchProfile } from "../../../core/data/redux/slices/ProfileSlice";
import api from "../../../core/axios/axiosInstance";

const CallerSettings = () => {
  const dispatch   = useDispatch();
  const userProfile = useSelector((state) => state.profile);

  // ── Caller Preferences ────────────────────────────────────────────────────
  const [defaultCallerNumber, setDefaultCallerNumber] = useState(userProfile?.defaultCallerNumber || null);
  const [askBeforeDialing, setAskBeforeDialing]       = useState(userProfile?.askBeforeDialing ?? false);
  const [showCallerName, setShowCallerName]           = useState(userProfile?.showCallerName || "number");
  const [callerPrefLoading, setCallerPrefLoading]     = useState(false);

  // Custom dropdown state
  const [callerDropdownOpen, setCallerDropdownOpen] = useState(false);
  const callerDropdownRef = useRef(null);

  // ── Number Names ─────────────────────────────────────────────────────────
  // Unified list: extensions + DIDs, all editable by company admin via didLabels
  const [numberLabels, setNumberLabels] = useState([]);
  const [labelsLoading, setLabelsLoading] = useState(false);

  // Click-outside for custom dropdown
  useEffect(() => {
    const handler = (e) => {
      if (callerDropdownRef.current && !callerDropdownRef.current.contains(e.target)) {
        setCallerDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Sync state when profile loads/changes
  useEffect(() => {
    setDefaultCallerNumber(userProfile?.defaultCallerNumber || null);
    setAskBeforeDialing(userProfile?.askBeforeDialing ?? false);
    setShowCallerName(userProfile?.showCallerName || "number");

    // Build unified number list — skip disabled extensions
    const extNums = (userProfile?.assignedExtensions || [])
      .filter((e) => e.enabled !== false)
      .map((e) => ({
        number: e.PBX_TELEPHONE,
        isExtension: true,
        extensionNumber: e.extensionNumber,
        adminNickname: e.nickname || "",
      }));
    const hasTel = userProfile?.telephone &&
      !(userProfile?.assignedExtensions || []).some((e) => e.PBX_TELEPHONE === userProfile.telephone);
    if (hasTel) {
      extNums.push({
        number: userProfile.telephone,
        isExtension: true,
        extensionNumber: userProfile.extensionNumber,
        adminNickname: "",
      });
    }
    const didNums = (userProfile?.purchasedDIDNumbers || [])
      .filter((num) => !extNums.some((e) => e.number === num))
      .map((num) => ({ number: num, isExtension: false, extensionNumber: null, adminNickname: "" }));

    const allNums = [...extNums, ...didNums];
    // Pre-fill nickname from didLabels (company admin names), fall back to adminNickname
    const didMap = Object.fromEntries((userProfile?.didLabels || []).map((d) => [d.number, d.nickname || ""]));
    setNumberLabels(allNums.map((n) => ({
      ...n,
      nickname: didMap[n.number] ?? n.adminNickname,
    })));
  }, [userProfile]);

  const handleSaveCallerPrefs = async () => {
    setCallerPrefLoading(true);
    try {
      await api.patch("/editProfile/caller-preferences", { defaultCallerNumber, askBeforeDialing, showCallerName });
      dispatch(fetchProfile());
      antMessage.success("Caller preferences saved");
    } catch {
      antMessage.error("Failed to save preferences");
    } finally {
      setCallerPrefLoading(false);
    }
  };

  const handleSaveNumberNames = async () => {
    setLabelsLoading(true);
    try {
      const payload = numberLabels.map((n) => ({ number: n.number, nickname: n.nickname }));
      await api.patch("/editProfile/did-labels", { didLabels: payload });
      dispatch(fetchProfile());
      antMessage.success("Number names saved");
    } catch {
      antMessage.error("Failed to save names");
    } finally {
      setLabelsLoading(false);
    }
  };

  // Build dropdown options — exclude disabled extensions
  const extNums = (userProfile?.assignedExtensions || [])
    .filter((e) => e.enabled !== false)
    .map((e) => ({
      value: e.PBX_TELEPHONE,
      label: e.nickname ? `${e.nickname} (${e.PBX_TELEPHONE})` : `Ext ${e.extensionNumber} — ${e.PBX_TELEPHONE}`,
    }));
  const hasTel = userProfile?.telephone &&
    !(userProfile?.assignedExtensions || []).some((e) => e.PBX_TELEPHONE === userProfile.telephone);
  const callerOpts = [
    { value: "", label: "— None (always ask) —" },
    ...extNums,
    ...(hasTel ? [{ value: userProfile.telephone, label: `Ext ${userProfile.extensionNumber} — ${userProfile.telephone}` }] : []),
  ];
  const selectedOpt = callerOpts.find((o) => o.value === (defaultCallerNumber || "")) || callerOpts[0];

  const hasNumbers = (
    (userProfile?.assignedExtensions || []).some((e) => e.enabled !== false) ||
    !!userProfile?.telephone
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            {/* Page title */}
            <div className="page-header mb-0">
              <div className="row align-items-center">
                <div className="col-sm-12">
                  <h4 className="page-title">Dialer Settings</h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        {!hasNumbers && (
          <div className="alert alert-info mt-3">
            No phone numbers or extensions are assigned to your account yet. Contact your administrator.
          </div>
        )}

        {hasNumbers && (
          <>
            {/* ── Caller Preferences ── */}
            <div className="card mt-3">
              <div className="card-body">
                <h4 className="fw-semibold mb-1">Caller Preferences</h4>
                <p className="text-muted small mb-3">
                  Controls which number is used when dialling from contact and lead lists.
                </p>

                {/* Default Caller Number — custom dropdown */}
                <div className="mb-3">
                  <label className="form-label fw-medium">Default Caller Number</label>
                  <div ref={callerDropdownRef} style={{ position: "relative" }}>
                    <button
                      type="button"
                      onClick={() => setCallerDropdownOpen((o) => !o)}
                      style={{
                        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "8px 12px", borderRadius: 8,
                        border: "1px solid #d1d5db", background: "#fff", cursor: "pointer",
                        fontSize: 14, color: defaultCallerNumber ? "#111827" : "#6b7280",
                        textAlign: "left",
                      }}
                    >
                      <span>{selectedOpt.label}</span>
                      <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>▾</span>
                    </button>
                    {callerDropdownOpen && (
                      <div style={{
                        position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 9999,
                        background: "#fff", border: "1px solid #e5e7eb",
                        borderRadius: 10, overflow: "hidden",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      }}>
                        {callerOpts.map((opt) => {
                          const isSel = opt.value === (defaultCallerNumber || "");
                          return (
                            <div
                              key={opt.value}
                              onClick={() => { setDefaultCallerNumber(opt.value || null); setCallerDropdownOpen(false); }}
                              style={{
                                padding: "9px 14px", cursor: "pointer", fontSize: 14,
                                background: isSel ? "#f3f4f6" : "transparent",
                                color: "#111827", fontWeight: isSel ? 600 : 400,
                                borderBottom: "1px solid #f3f4f6",
                              }}
                              onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = "#f9fafb"; }}
                              onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = "transparent"; }}
                            >
                              {opt.label}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="form-text text-muted mt-1">
                    Used when &ldquo;Ask Before Dialing&rdquo; is off.
                  </div>
                </div>

                {/* Ask Before Dialing */}
                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-medium">Ask Before Dialing</div>
                      <div className="text-muted small">
                        Show a caller selection prompt on every outbound call (when you have more than 1 number).
                      </div>
                    </div>
                    <Switch
                      checked={askBeforeDialing}
                      onChange={setAskBeforeDialing}
                      checkedChildren="On"
                      unCheckedChildren="Off"
                    />
                  </div>
                </div>

                {/* Dialer Display Mode */}
                <div className="mb-4">
                  <label className="form-label fw-medium">Dialer Display</label>
                  <div className="text-muted small mb-2">
                    Choose what is shown in the "Calling From" selector inside the dialer.
                  </div>
                  <Segmented
                    value={showCallerName}
                    onChange={setShowCallerName}
                    options={[
                      { label: "Number only", value: "number" },
                      { label: "Name only",   value: "name" },
                      { label: "Name + Number", value: "name_number" },
                    ]}
                    block
                  />
                  <div className="form-text text-muted mt-1">
                    {showCallerName === "number"      && "Shows the raw number (e.g. +1 555 0100)."}
                    {showCallerName === "name"        && "Shows the custom name only (e.g. Sales 1). Falls back to number if no name is set."}
                    {showCallerName === "name_number" && "Shows the custom name and number together (e.g. Sales 1 (+1 555 0100))."}
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ width: 160 }}
                  disabled={callerPrefLoading}
                  onClick={handleSaveCallerPrefs}
                >
                  {callerPrefLoading ? "Saving…" : "Save Preferences"}
                </button>
              </div>
            </div>

            {/* ── Number Names ── */}
            {numberLabels.length > 0 && (
              <div className="card mt-3">
                <div className="card-body">
                  <h4 className="fw-semibold mb-1">Number Names</h4>
                  <p className="text-muted small mb-3">
                    Give your numbers friendly names. Used when Dialer Display is set to "Name only" or "Name + Number".
                    Your names override any admin-set names.
                  </p>

                  {numberLabels.map((item, idx) => (
                    <div key={item.number} className="d-flex align-items-center gap-2 mb-2">
                      <div style={{ minWidth: 150 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>{item.number}</div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>
                          {item.isExtension ? `Ext ${item.extensionNumber}` : "DID Number"}
                        </div>
                      </div>
                      <input
                        className="form-control form-control-sm"
                        placeholder={item.adminNickname ? `Admin name: "${item.adminNickname}"` : "Add a name…"}
                        value={item.nickname}
                        onChange={(e) => {
                          const updated = numberLabels.map((n, i) =>
                            i === idx ? { ...n, nickname: e.target.value } : n
                          );
                          setNumberLabels(updated);
                        }}
                      />
                    </div>
                  ))}

                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm mt-2"
                    disabled={labelsLoading}
                    onClick={handleSaveNumberNames}
                  >
                    {labelsLoading ? "Saving…" : "Save Names"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CallerSettings;
